/**
 * Safe local category suggestion helper.
 *
 * Important:
 * - Uses only domain + optional page title.
 * - Does NOT fetch webpage content.
 * - Does NOT call any external service.
 * - Intended for suggestions only (not enforcement).
 */

import { CATEGORY_KEYWORD_RULES, CATEGORY_NAMES } from "../blocking/categoryModel";

const CATEGORY_LIST = CATEGORY_NAMES;
const MAX_TITLE_CHARS = 160;

const DOMAIN_SIGNALS = {
  Adult: [...(CATEGORY_KEYWORD_RULES.Adult || []), "onlyfans", "cam", "escort"],
  Gambling: [...(CATEGORY_KEYWORD_RULES.Gambling || []), "roulette", "sportsbook", "bookmaker", "wager"],
  "Social Media": [...(CATEGORY_KEYWORD_RULES["Social Media"] || []), "social", "discord"],
  Gaming: [...(CATEGORY_KEYWORD_RULES.Gaming || []), "fortnite", "esports"],
};

const TITLE_SIGNALS = {
  Adult: ["adult", "18+", "sex", "porn", "hentai", "nsfw"],
  Gambling: ["casino", "bet", "poker", "slots", "jackpot", "wager"],
  "Social Media": ["social", "followers", "posts", "stories", "feed"],
  Gaming: ["game", "gaming", "live stream", "stream", "esports", "match"],
};

function normalizeHost(input) {
  const raw = String(input || "").trim().toLowerCase();
  if (!raw) return "";
  let s = raw.replace(/^\s*https?:\/\//i, "");
  s = s.replace(/^www\./, "");
  s = s.split(/[/?#]/)[0];
  return s.replace(/\.+$/, "");
}

function safeHostFromDomainInput(domainOrUrl) {
  const raw = String(domainOrUrl || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`);
    return normalizeHost(url.hostname);
  } catch {
    return normalizeHost(raw);
  }
}

function sanitizeTitle(input) {
  // Keep this helper privacy-safe: no page content extraction, only a short provided title string.
  let t = String(input || "");
  t = t.replace(/<[^>]*>/g, " "); // strip HTML tags if any
  t = t.replace(/https?:\/\/\S+/gi, " "); // strip embedded URLs
  t = t.replace(/\s+/g, " ").trim().toLowerCase();
  if (t.length > MAX_TITLE_CHARS) t = t.slice(0, MAX_TITLE_CHARS);
  return t;
}

function scoreSignals(text, signals, weight) {
  let score = 0;
  const hits = [];
  for (const sig of signals) {
    if (!sig) continue;
    if (text.includes(sig.toLowerCase())) {
      score += weight;
      hits.push(sig);
    }
  }
  return { score, hits };
}

/**
 * Suggest category from domain + optional page title.
 * Returns only: Adult | Gambling | Social Media | Gaming | Unknown
 */
export function suggestCategoryForDomain(domainOrUrl, pageTitle = "") {
  return suggestCategoryForDomainDetailed(domainOrUrl, pageTitle).predictedCategory;
}

/**
 * Detailed variant for UI/debugging.
 */
export function suggestCategoryForDomainDetailed(domainOrUrl, pageTitle = "") {
  const host = safeHostFromDomainInput(domainOrUrl);
  const title = sanitizeTitle(pageTitle);

  if (!host) {
    return {
      predictedCategory: "Unknown",
      confidence: 0,
      reason: "empty-input",
      matchedSignals: [],
    };
  }

  const totals = {};
  const hits = {};
  for (const c of CATEGORY_LIST) {
    totals[c] = 0;
    hits[c] = [];
  }

  for (const category of CATEGORY_LIST) {
    const domainRes = scoreSignals(host, DOMAIN_SIGNALS[category] || [], 3);
    totals[category] += domainRes.score;
    hits[category].push(...domainRes.hits.map((h) => `domain:${h}`));

    if (title) {
      const titleRes = scoreSignals(title, TITLE_SIGNALS[category] || [], 1);
      totals[category] += titleRes.score;
      hits[category].push(...titleRes.hits.map((h) => `title:${h}`));
    }
  }

  let bestCategory = "Unknown";
  let bestScore = 0;
  for (const category of CATEGORY_LIST) {
    if (totals[category] > bestScore) {
      bestScore = totals[category];
      bestCategory = category;
    }
  }

  if (bestScore < 3) {
    return {
      predictedCategory: "Unknown",
      confidence: 0,
      reason: "insufficient-signal",
      matchedSignals: [],
    };
  }

  const confidence = Math.min(1, bestScore / 9);
  return {
    predictedCategory: bestCategory,
    confidence,
    reason: "signal-match",
    matchedSignals: hits[bestCategory].slice(0, 6),
  };
}

